import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useProgressNotifierContext } from '@trukt/ui/ProgressNotifierContext';
import { getErrorMessage } from '@trukt/portal-utils/getErrorMessage';
import { v1 as uuid } from 'uuid';
import { QUERY_CACHE_IDS_MAP } from '@types';
import { GetReportResponse, truktApi } from '@api';
import { UseReportQueryResult } from './useReportQuery.types';
import { AxiosResponse } from 'axios';

type UseReportQueryOptions = {
  onSuccess?: (data: AxiosResponse<GetReportResponse>) => void;
};

export const useReportQuery = (
  reportGuid: string,
  options: UseReportQueryOptions = {},
): UseReportQueryResult => {
  const progressNotifier = useProgressNotifierContext();
  const onSuccessRef = useRef(options.onSuccess);

  useEffect(() => {
    onSuccessRef.current = options.onSuccess;
  }, [options.onSuccess]);

  const query = useQuery({
    queryKey: [QUERY_CACHE_IDS_MAP.REPORT, reportGuid],

    queryFn: () => {
      return truktApi.report.getById(reportGuid);
    },

    enabled: Boolean(reportGuid.length),
  });

  useEffect(() => {
    if (query.data) onSuccessRef.current?.(query.data);
  }, [query.data]);

  useEffect(() => {
    if (query.error) {
      progressNotifier.dispatchNotification({
        id: uuid(),
        isError: true,
        label: 'Unable to get report.',
        errorMessage: getErrorMessage(query.error),
      });
    }
  }, [progressNotifier, query.error]);

  return query;
};
